import { altchaWidgetHtml, captchaPromptInstructions } from './altcha';
import type { FormField, FormSpec } from './form-specs';

function translateTypeToHtmlType(type: string) {
  switch (type) {
    case 'text':
      return 'input';
    case 'textarea':
      return 'textarea';
    case 'select':
      return 'select';
    case 'checkbox':
      return 'input';
    case 'radio':
      return 'input';
    case 'file':
      return 'input';
    case 'hidden':
      return 'input';
    case 'submit':
      return 'button';
    case 'number':
      return 'input';
    case 'date':
      return 'input';
    default:
      return 'input';
  }
}

function attachLabelAndHelpText(
  html: string,
  fieldName: string,
  inset: number,
  label?: string,
  helptext?: string,
  required?: boolean
) {
  const insetHtml = ' '.repeat(inset);
  if (label) {
    const requiredIndicator = required ? '*' : '';
    html = `${insetHtml}<label for="${fieldName}">${label}${requiredIndicator}</label>
${html}`;
  }

  if (helptext) {
    html = `${html}
${insetHtml}<br/><small>${helptext}</small>`;
  }

  return html;
}

const join = (...parts: string[]) => parts.filter(Boolean).join(' ');

// A group of choices has no single input for a <label for>, so it is a fieldset with a legend.
function renderFieldOptions(field: FormField, properties: string, insetHtml: string) {
  const options = field.options ? field.options.split(',') : [];
  const legend = field.label
    ? `${insetHtml}  <legend>${field.label}${field.required ? '*' : ''}</legend>\n`
    : '';
  const inputs = options
    .map(
      (option) =>
        `${insetHtml}  <label><input ${join(`type="${field.field}"`, `name="${field.name}"`, `value="${option.replace(' ', '_')}"`, properties)}/> ${option}</label>`
    )
    .join('\n');
  const help = field.helptext ? `\n${insetHtml}  <small>${field.helptext}</small>` : '';
  return `${insetHtml}<fieldset>\n${legend}${inputs}${help}\n${insetHtml}</fieldset>`;
}

const escapeAttr = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

// `disabled` and `readonly` apply to the hosted form only; a disabled input is never sent.
function fieldProperties(field: FormField): string {
  const props: string[] = [];
  if (field.required) props.push('required');
  if (field.multiple) props.push('multiple');
  return props.join(' ');
}

// Browser hints only; the server checks the same rules regardless.
function constraintAttributes(field: FormField): string {
  const attrs: string[] = [];
  if (field.field === 'text' || field.field === 'textarea') {
    if (field.is_min !== undefined) attrs.push(`minlength="${field.is_min}"`);
    if (field.is_max !== undefined) attrs.push(`maxlength="${field.is_max}"`);
  } else if (field.field === 'number') {
    if (field.is_min !== undefined) attrs.push(`min="${field.is_min}"`);
    if (field.is_max !== undefined) attrs.push(`max="${field.is_max}"`);
  }
  if (field.is_pattern && ['text', 'email', 'url', 'tel'].includes(field.field)) {
    attrs.push(`pattern="${escapeAttr(field.is_pattern)}"`);
  }
  return attrs.join(' ');
}

export function renderInputField(field: FormField, inset = 2) {
  const type = translateTypeToHtmlType(field.field);
  const properties = fieldProperties(field);
  const inputType =
    field.is_email === true ? 'email' : field.field === 'datetime' ? 'datetime-local' : field.field;
  const insetHtml = ' '.repeat(inset);
  const placeholder = field.placeholder ? `placeholder="${escapeAttr(field.placeholder)}"` : '';

  let html = '';

  if (field.hidden) {
    // Off-screen rather than display:none, which some bots skip; out of tab order for people.
    const input = `${insetHtml}  <input type="${inputType}" id="${field.name}" name="${field.name}" tabindex="-1" autocomplete="off"/>`;
    const label = field.label
      ? `${insetHtml}  <label for="${field.name}">${field.label}</label>\n`
      : '';
    return `${insetHtml}<div style="position:absolute;left:-9999px" aria-hidden="true">\n${label}${input}\n${insetHtml}</div>`;
  }

  if (field.field === 'select') {
    const options = field.options ? field.options.split(',') : [];
    html = `${insetHtml}<${type} ${join(`id="${field.name}"`, `name="${field.name}"`, placeholder, properties)}>
  ${options.map((option) => `${insetHtml}<option value="${option}">${option}</option>`).join('')}
${insetHtml}</${type}>`;
  } else if (field.field === 'radio' || field.field === 'checkbox') {
    return renderFieldOptions(field, properties, insetHtml);
  } else if (field.field === 'submit') {
    return `${insetHtml}<button type="submit" value="submit">${field.label}</button>`;
  } else if (field.field === 'textarea') {
    html = `${insetHtml}<${type} ${join(`id="${field.name}"`, `name="${field.name}"`, placeholder, constraintAttributes(field), properties)}>${field.value || ''}</${type}>`;
  } else if (field.field === 'hidden') {
    const value = field.value !== undefined ? `value="${escapeAttr(String(field.value))}"` : '';
    return `${insetHtml}<input ${join('type="hidden"', `name="${field.name}"`, value)}/>`;
  } else {
    html = `${insetHtml}<${type} ${join(`type="${inputType}"`, `id="${field.name}"`, `name="${field.name}"`, placeholder, constraintAttributes(field), properties)}/>`;
  }

  return attachLabelAndHelpText(
    html,
    field.name,
    inset,
    field.label,
    field.helptext,
    field.required
  );
}

export const generateHtmlFromSpecV2 = (
  data: FormSpec,
  formUrl: string,
  checkChallenge: boolean,
  challengeUrl: string,
  text: { captchaNote: string; submit: string }
): string => {
  const hasFileUpload = Object.values(data.fields).some((field) => field.field === 'file');
  const encType = hasFileUpload ? 'multipart/form-data' : 'application/x-www-form-urlencoded';

  let formHtml = `<form action="${formUrl}" method="POST" enctype="${encType}">\n`;
  for (const field of Object.values(data.fields)) {
    formHtml += `${renderInputField(field)}\n`;
  }

  // Add captcha widget if checkChallenge is true
  if (checkChallenge) {
    formHtml += `\n`;
    formHtml += `  <!-- ${text.captchaNote} -->\n`;
    formHtml += `${altchaWidgetHtml(challengeUrl, '  ')}\n`;
  }

  formHtml += `  <button type="submit" value="submit">${text.submit}</button>\n`;
  formHtml += '</form>';

  return formHtml;
};

// export function exampleLLMPrompt(formUrl: string) {
//   return `Generate a HTML form with following fields:
//   - email: required
//   - name: required
//   - message: required
//   - products: optional, multiple checkboxes (Product A, Product B, Product C)

// and a button to submit ("Send").

// POST to the following URL: ${formUrl}`;
// }

export const generateLLMPrompt = (
  data: FormSpec,
  formUrl: string,
  checkChallenge: boolean,
  challengeUrl: string
): string => {
  const fields = Object.entries(data.fields)
    .map(([key, field]) => {
      const fieldType = field.field;
      const fieldLabel = field.label || field.name;
      const fieldRequired = field.required ? 'required' : 'optional';
      return `- ${fieldLabel} (name="${key}", type: ${fieldType}): ${fieldRequired}`;
    })
    .join('\n');

  let prompt = `Generate a HTML form with following fields:\n${fields}\n\n`;

  if (checkChallenge) {
    prompt += captchaPromptInstructions(challengeUrl);
  }

  prompt += `POST to the following URL: ${formUrl}`;

  return prompt;
};
